import { FormManager } from 'react-form-state-manager';
import { Globe } from 'react-feather';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Locale } from '__generated__/graphql';

import { ButtonProps } from '../../common/UI/Button';
import { DebouncedInputProps } from '../../common/UI/DebouncedInput';
import { EditorProps } from '../../common/Tiptap/helpers';
import { ErrorBag } from '../../common/helpers';
import { MenuBarButton } from '../../common/Tiptap/Layout';
import { TextAreaProps } from '../../common/UI/TextArea';
import { Translation, countTranslations } from './helpers';
import Editor from '../../common/Tiptap';
import UI from '../../common/UI';
import useEditTranslationsModal from './useEditTranslationsModal';

export interface TranslatableInputProps extends Omit<DebouncedInputProps, 'form'> {
  form: FormManager;
  errors: ErrorBag;
  name: string;
  defaultLocale: Locale;
  title?: string;
  inputPrefix?: ReactNode;
}

export const TranslatableInput = ({
  form, errors, name, defaultLocale, title, maxLength, inputPrefix, ...props
}: TranslatableInputProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const defaultValue = form.get(name);
  const translations: Translation[] = form.get(`${name}_translations`);
  const translationsCount = countTranslations(translations, defaultLocale);

  const translationErrors = translations.reduce((errorBag, translation, index) => {
    const error = errors[`${name}_translations.${index}.value`];
    if (error) {
      errorBag[translation.locale] = error;
    }

    return errorBag;
  }, {} as ErrorBag);

  const hasErrors = Object.keys(translationErrors).length > 0;

  const [openModal] = useEditTranslationsModal({
    defaultLocale,
    defaultValue,
    translations,
    maxLength,
    errors: translationErrors,
    title,
    onSave: (defaultValue, translations) => {
      form.set(name, defaultValue);
      form.set(`${name}_translations`, translations);

      inputRef.current.value = defaultValue;
    },
  });

  const [compact, setCompact] = useState(true);

  useEffect(() => {
    if (inputRef.current) {
      setCompact(inputRef.current.clientWidth < 200);
    }
  }, [inputRef]);

  return (
    <UI.InputWrapper>
      {inputPrefix && (
        <UI.Span>
          {inputPrefix}
        </UI.Span>
      )}
      <UI.DebouncedInput
        {...form.text(name)}
        maxLength={maxLength}
        {...props}
        ref={inputRef}
      />
      <TranslateButton
        onClick={openModal}
        title={title}
        count={translationsCount}
        input={inputRef.current}
        defaultLocale={defaultLocale}
        hasErrors={hasErrors}
        compact={compact}
      />
    </UI.InputWrapper>
  );
};

export interface TranslatableEditorProps extends Partial<EditorProps> {
  form: FormManager;
  errors: ErrorBag;
  name: string;
  defaultLocale: Locale;
  brandColor?: string;
  title?: string;
}

export const TranslatableEditor = ({
  form, errors, name, defaultLocale, title, ...editorProps
}: TranslatableEditorProps) => {
  const { t } = useTranslation();

  const defaultValue = form.get(name);
  const translations: Translation[] = form.get(`${name}_translations`);
  const translationsCount = countTranslations(translations, defaultLocale);

  const translationErrors = translations.reduce((errorBag, translation, index) => {
    const error = errors[`${name}_translations.${index}.value`];
    if (error) {
      errorBag[translation.locale] = error;
    }

    return errorBag;
  }, {} as ErrorBag);

  const hasErrors = Object.keys(translationErrors).length > 0;

  const [key, setKey] = useState(0);

  const [openModal] = useEditTranslationsModal({
    defaultLocale,
    defaultValue,
    translations,
    errors: translationErrors,
    title,
    inputType: 'text',
    editorProps: {
      ...editorProps,
      generateContent: undefined,
    },
    onSave: (defaultValue, translations) => {
      form.set(name, defaultValue);
      form.set(`${name}_translations`, translations);

      setKey((key) => key + 1);
    },
  });

  return (
    <Editor
      value={form.get(name)}
      onChange={(value) => form.set(name, value)}
      translateButton={(compact, disabled) => (
        <MenuBarButton
          onClick={openModal}
          aria-label={t(title ? 'translate_option' : 'translate', { option: title })}
          disabled={disabled}
        >
          <UI.Span sc={{ color: hasErrors ? 'error.500' : undefined }}>
            <UI.Icon sc={{ mb: 0.4 }}>
              <Globe />
            </UI.Icon>
            {!compact && ` ${t(`common:locales.${defaultLocale}`)}`}
            {translationsCount > 0 && (compact ? ` +${translationsCount}` : ` (+${translationsCount})`)}
          </UI.Span>
        </MenuBarButton>
      )}
      key={key}
      {...editorProps}
    />
  );
};

export interface TranslatableTextAreaProps extends Omit<TextAreaProps, 'form'> {
  form: FormManager;
  errors: ErrorBag;
  name: string;
  defaultLocale: Locale;
  title?: string;
}

export const TranslatableTextArea = ({
  form, errors, name, defaultLocale, title, maxLength, ...props
}: TranslatableTextAreaProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement>();
  const defaultValue = form.get(name);
  const translations: Translation[] = form.get(`${name}_translations`);
  const translationsCount = countTranslations(translations, defaultLocale);

  const translationErrors = translations.reduce((errorBag, translation, index) => {
    const error = errors[`${name}_translations.${index}.value`];
    if (error) {
      errorBag[translation.locale] = error;
    }

    return errorBag;
  }, {} as ErrorBag);

  const hasErrors = Object.keys(translationErrors).length > 0;

  const [openModal] = useEditTranslationsModal({
    defaultLocale,
    defaultValue,
    translations,
    inputType: 'textarea',
    maxLength,
    errors: translationErrors,
    title,
    onSave: (defaultValue, translations) => {
      form.set(name, defaultValue);
      form.set(`${name}_translations`, translations);

      inputRef.current.value = defaultValue;
    },
  });

  return (
    <div>
      <UI.FlexContainer
        sc={{ justifyContent: 'end', relative: true }}
        style={{ padding: 10, marginBottom: -51, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}
        onClick={() => inputRef.current?.focus()}
      >
        <MenuBarButton
          onClick={(event) => { event.stopPropagation(); openModal(); }}
          aria-label={t(title ? 'translate_option' : 'translate', { option: title })}
        >
          <UI.Span sc={{ color: hasErrors ? 'error.500' : undefined }}>
            <UI.Icon sc={{ mb: 0.4 }}>
              <Globe />
            </UI.Icon>
            {' '}
            {t(`common:locales.${defaultLocale}`)}
            {translationsCount > 0 && ` (+${translationsCount})`}
          </UI.Span>
        </MenuBarButton>
      </UI.FlexContainer>
      <UI.TextArea
        {...form.textarea(name)}
        maxLength={maxLength}
        {...props}
        ref={inputRef}
        style={{ paddingTop: 62 }}
      />
    </div>
  );
};

interface TranslateButtonProps {
  onClick: () => void;
  title?: string;
  count?: number;
  defaultLocale: Locale;
  hasErrors?: boolean;
  compact?: boolean;
  input?: HTMLInputElement;
  sc?: ButtonProps;
}

export const TranslateButton = ({
  onClick, title, defaultLocale, count = 0, hasErrors = false, compact = false, input, sc,
}: TranslateButtonProps) => {
  const { t } = useTranslation();

  const buttonRef = useRef<HTMLButtonElement>();

  useEffect(() => {
    if (input) {
      const { paddingRight } = input.style;
      input.style.paddingRight = `${Math.max((buttonRef.current?.offsetWidth || 0) + 10, 0)}px`;

      return () => {
        input.style.paddingRight = `${paddingRight}px`;
      };
    }

    return () => {};
  });

  return (
    <UI.Button
      onClick={onClick}
      sc={{
        blank: true, color: hasErrors ? 'error.500' : 'gray.800', small: true, ...sc,
      }}
      aria-label={t(title ? 'translate_option' : 'translate', { option: title })}
      ref={buttonRef}
    >
      <UI.Icon sc={{ mb: 0.4 }}>
        <Globe />
      </UI.Icon>
      {!compact && ` ${t(`common:locales.${defaultLocale}`)}`}
      {count > 0 && (compact ? ` +${count}` : ` (+${count})`)}
    </UI.Button>
  );
};

export default undefined;
